import UnfoldLessRounded from "@mui/icons-material/UnfoldLessRounded";
import UnfoldMoreRounded from "@mui/icons-material/UnfoldMoreRounded";
import {
  Box,
  ButtonBase,
  FormControlLabel,
  IconButton,
  Switch,
  Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import AssociationsEditor from "../../../components/associations/AssociationsEditor";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { useLoanFilesHook } from "../../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import { slate, teal } from "../../../theme/colors";
import DocGroupCoverageCard from "./DocGroupCoverageCard";
import RecordDocumentsDropzone from "./RecordDocumentsDropzone";

// Doc groups per record type — the same lists the borrower / property upload
// processes pass as `allowedDocGroups` (and the Applicants tab's files table).
export const BORROWER_DOC_GROUPS = [
  "Borrower Documents",
  "Borrower Reports",
  "Experience and Track Record",
  "Liquidity",
  "Related Entity Docs",
];
export const PROPERTY_DOC_GROUPS = [
  "Appraisal",
  "Construction Documents",
  "Insurance Docs",
  "Leases & Rents",
  "Property Documents",
  "Title Docs",
];

const typeOf = (doc) => doc?.docType || "Uncategorized";

// How tall the documents panel may grow with its content on a record card
// before it scrolls (it is never shorter than the card it sits in).
export const DOC_PANEL_MAX_H = 640;

// The Expand all / Collapse all icon buttons — a bordered square that matches
// the old outlined button's height.
const ICON_BTN_SX = {
  p: "5px",
  borderRadius: "8px",
  border: `1px solid ${slate[200]}`,
  backgroundColor: "#ffffff",
  color: slate[600],
  "&:hover": { backgroundColor: slate[50] },
  "&.Mui-disabled": { color: slate[300], borderColor: slate[100] },
};

/* ═══════════════════════════════════════════════════════════
   RECORD DOC COVERAGE PANEL — the documents column of one ApplicantCard /
   PropertyCard, drawn as the Documents tab's DocGroup Coverage (the Entity
   rail's shape): one coverage card per doc group for the record type,
   counting ONLY this record's associated files.

   • "Show missing" reveals the document types with nothing on file — and the
     groups that are entirely empty for this record. On a property it always
     starts OFF; on an applicant it starts ON when the record has no
     documents, OFF otherwise.
   • An upload box always sits above the list: a roomier one with nothing on
     file, a one-line strip once there are documents.
   • Expand all / Collapse all (two icon buttons) open / fold every group and every type that
     has files.
   • Upload New on a type stages into the tab's upload queue pre-assigned to
     the record with the group + type set; "Assign Existing" opens the link
     editor searched to that type.

   A group the record has files in but that isn't on its list still gets a
   card, so nothing associated to the record disappears.

   Trial swap for RecordDocumentsPanel — either card can switch back by
   rendering that component again with the same props.
   ═══════════════════════════════════════════════════════════ */
const RecordDocCoveragePanel = ({
  assignTo,
  docGroups,
  files,
  linkedDocumentIds,
  masonry = false,
  sx,
}) => {
  const dispatch = useDispatch();
  const loanId = useSelector((s) => s.loanDrilldown?._id);
  const loanDrilldown = useSelector((s) => s.loanDrilldown);
  const { loading: linking, associateDocumentWithObjects } = useLoanFilesHook();

  const isProperty = assignTo?.type === "property";
  // Defaults to the record type's own group list.
  const baseGroups =
    docGroups || (isProperty ? PROPERTY_DOC_GROUPS : BORROWER_DOC_GROUPS);

  // Properties always open with Show missing OFF. An applicant with nothing on
  // file opens with every document type listed, so there is something to
  // upload against straight away.
  const [showMissing, setShowMissing] = useState(
    () => !isProperty && !(files?.length > 0),
  );
  // The list has been scrolled — shows the top fade under the header.
  const [scrolled, setScrolled] = useState(false);
  // More content below the fold — shows the bottom fade. Re-measured on
  // scroll and whenever the list's size changes (groups folding, files
  // arriving), since either can create or remove the overflow.
  const [moreBelow, setMoreBelow] = useState(false);
  // The panel shown at its full height (no inner scroll) — toggled from the
  // bottom fade.
  const [fullHeight, setFullHeight] = useState(false);
  const scrollRef = useRef(null);
  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setScrolled(el.scrollTop > 0);
    setMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    Array.from(el.children).forEach((c) => ro.observe(c));
    measure();
    return () => ro.disconnect();
  });
  // group → Set of open doc-type names.
  const [openByGroup, setOpenByGroup] = useState({});
  // Groups folded shut from their header. Open by default — except on a
  // record with nothing assigned, where Show missing lists every group and
  // they start folded so the rail reads as a short list of groups.
  const [collapsedGroups, setCollapsedGroups] = useState(() =>
    files?.length > 0 ? new Set() : new Set(baseGroups),
  );
  const [assignTarget, setAssignTarget] = useState(null);

  // group → Set of doc types this record has files in.
  const filledTypes = useMemo(() => {
    const out = {};
    (files || []).forEach((doc) => {
      const g = doc?.docGroup || "";
      if (!g) return;
      if (!out[g]) out[g] = new Set();
      out[g].add(typeOf(doc));
    });
    return out;
  }, [files]);

  const groups = useMemo(() => {
    const extra = Object.keys(filledTypes)
      .filter((g) => !baseGroups.includes(g))
      .sort();
    const all = [...baseGroups, ...extra];
    return showMissing ? all : all.filter((g) => filledTypes[g]?.size);
  }, [filledTypes, showMissing, baseGroups]);

  // "Expanded" = every shown group open and every type with files open.
  const allExpanded =
    groups.length > 0 &&
    groups.every((g) => !collapsedGroups.has(g)) &&
    Object.entries(filledTypes).every(([g, types]) =>
      [...types].every((t) => openByGroup[g]?.has(t)),
    );

  // "Collapsed" = every shown group folded shut.
  const allCollapsed =
    groups.length > 0 && groups.every((g) => collapsedGroups.has(g));

  const collapseAll = () => {
    setOpenByGroup({});
    setCollapsedGroups(new Set(groups));
  };

  const expandAll = () => {
    setCollapsedGroups(new Set());
    const next = {};
    Object.entries(filledTypes).forEach(([g, types]) => {
      next[g] = new Set(types);
    });
    setOpenByGroup(next);
  };

  const toggleGroup = useCallback((group) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.has(group) ? next.delete(group) : next.add(group);
      return next;
    });
  }, []);

  const toggleType = useCallback((group, name) => {
    setOpenByGroup((prev) => {
      const set = new Set(prev[group] || []);
      set.has(name) ? set.delete(name) : set.add(name);
      return { ...prev, [group]: set };
    });
  }, []);

  const onAssign = useCallback(
    (group, docType) => setAssignTarget({ group, docType }),
    [],
  );

  // Same save path as RecordDocumentsPanel's link editor: only newly-checked
  // ids come back, and the backend pushes them onto the record.
  const handleAssignSave = useCallback(
    (selected) => {
      const newDocIds = selected?.loanDocuments || [];
      if (newDocIds.length === 0 || !assignTo?.id) {
        setAssignTarget(null);
        return;
      }
      associateDocumentWithObjects({
        dealId: loanId,
        loanDocumentsIds: newDocIds,
        borrowerIds: isProperty ? undefined : [assignTo.id],
        subjectPropertyIds: isProperty ? [assignTo.id] : undefined,
        onSuccessFn: (d) => {
          dispatch(loanDrilldownSet(d));
          setAssignTarget(null);
        },
      });
    },
    [associateDocumentWithObjects, loanId, assignTo?.id, isProperty, dispatch],
  );

  // Ids already on the record — the link editor pre-checks and locks them.
  const linkedIds = useMemo(
    () => linkedDocumentIds || (files || []).map((f) => f?._id).filter(Boolean),
    [linkedDocumentIds, files],
  );
  const fileCount = files?.length || 0;

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minWidth: 0,
        ...sx,
        // "Show full height": drop out of the card's fixed frame (an
        // absolutely-positioned fill) into normal flow, so the card grows to
        // the whole list instead of scrolling it.
        ...(fullHeight && {
          position: "relative",
          inset: "auto",
          maxHeight: "none",
        }),
      }}
    >
      {/* Header */}
      <Flx ac jb g={1} sx={{ px: 2.25, pt: 2, pb: 1.25, flexShrink: 0 }}>
        <Flx ac g={1}>
          <Txt sx={{ fontSize: 16, color: teal[900], fontWeight: 700 }}>
            Assigned Documents
          </Txt>
          {/* Always shown — "0" says plainly that nothing is assigned. */}
          <Box
            sx={{
              px: 0.9,
              py: 0.1,
              borderRadius: "999px",
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1.6,
              color: fileCount ? teal[700] : slate[500],
              backgroundColor: fileCount ? teal[100] : slate[100],
            }}
          >
            {fileCount}
          </Box>
        </Flx>
        <Flx ac g={0.75}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showMissing}
                onChange={(e) => setShowMissing(e.target.checked)}
              />
            }
            label="Show missing"
            sx={{
              m: 0,
              mr: 0.5,
              flexShrink: 0,
              "& .MuiFormControlLabel-label": {
                fontSize: 11.5,
                color: slate[600],
                ml: 0.25,
              },
            }}
          />
          {/* Two icon buttons rather than one toggling label: each does one
              thing, and is disabled when there is nothing left for it to do. */}
          <Tooltip title="Expand all">
            <span>
              <IconButton
                size="small"
                aria-label="Expand all"
                disabled={groups.length === 0 || allExpanded}
                onClick={expandAll}
                sx={ICON_BTN_SX}
              >
                <UnfoldMoreRounded sx={{ fontSize: 17 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Collapse all">
            <span>
              <IconButton
                size="small"
                aria-label="Collapse all"
                disabled={groups.length === 0 || allCollapsed}
                onClick={collapseAll}
                sx={ICON_BTN_SX}
              >
                <UnfoldLessRounded sx={{ fontSize: 17 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Flx>
      </Flx>

      {/* The list's frame: the bottom fade + full-height toggle sit on
          THIS (absolutely, at its very bottom edge), not inside the scrolling
          list — a sticky child of the list is held above its 18px bottom
          padding, which left the fade floating short of the bottom. */}
      <Box
        sx={{
          position: "relative",
          flexGrow: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          "&:hover .doc-fade-toggle": { opacity: 1 },
        }}
      >
        <Box
          ref={scrollRef}
          onScroll={measure}
          sx={{
            overflowY: fullHeight ? "visible" : "auto",
            flexGrow: 1,
            minHeight: 0,
            px: 2.25,
            pb: fullHeight ? 5 : 2.25,
          }}
        >
          {/* Soft white fade under the header once the list is scrolled, so
            rows slide out of view instead of being cut off at a hard line.
            Sticky + zero net height (mb cancels it), so it never shifts the
            list; hidden at the top so the first card's edge stays crisp. */}
          <Box
            aria-hidden
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              height: 20,
              mb: "-20px",
              mx: -2.25,
              pointerEvents: "none",
              background:
                "linear-gradient(to bottom, #ffffff 0%, rgba(255,255,255,0.85) 40%, rgba(255,255,255,0) 100%)",
              opacity: scrolled ? 1 : 0,
              transition: "opacity 0.15s",
            }}
          />
          {/* Upload box — always above the list. Drop or click: files stage in
            the tab's upload queue pre-assigned to this record (the type is
            picked there). With nothing on file it is the roomier empty state;
            once there are documents it shrinks to a one-line strip. The
            per-type Upload / Assign buttons live on the rows below. */}
          <RecordDocumentsDropzone
            assignTo={assignTo}
            sx={{
              mb: groups.length ? 1.5 : 0,
              ...(fileCount ? { py: 1 } : { py: 2.5 }),
            }}
          >
            {/* Text only — no icon, no title. */}
            {/* One short line in both states; ellipsis rather than wrap if the
              rail is ever narrower than the sentence. */}
            <Txt
              component="div"
              sx={{
                fontSize: 12,
                color: slate[500],
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
              }}
            >
              Drop or click to upload, or upload by type below
            </Txt>
          </RecordDocumentsDropzone>

          {groups.length > 0 &&
            (() => {
              const cards = groups.map((group) => (
                <DocGroupCoverageCard
                  key={group}
                  group={group}
                  docs={files}
                  hideEmpty={!showMissing}
                  assignTo={assignTo}
                  onAssign={onAssign}
                  openTypes={openByGroup[group]}
                  onToggleType={(name) => toggleType(group, name)}
                  open={!collapsedGroups.has(group)}
                  onToggleOpen={() => toggleGroup(group)}
                />
              ));
              // Masonry: the Documents tab's DocGroup Coverage grid — cards
              // capped near 330px wide, flowing into as many columns as fit.
              return masonry ? (
                <Box
                  sx={{
                    columnWidth: 330,
                    columnGap: "14px",
                    "& > *": { breakInside: "avoid", mb: "10px" },
                  }}
                >
                  {cards.map((card) => (
                    <Box key={card.key}>{card}</Box>
                  ))}
                </Box>
              ) : (
                <Flx column g={1.25}>
                  {cards}
                </Flx>
              );
            })()}
        </Box>

        {/* Bottom fade (only while more is below) and, on hover, the toggle
            between the capped and the full-height panel. */}
        {(moreBelow || fullHeight) && (
          <Flx
            jc
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 44,
              alignItems: "flex-end",
              pb: "8px",
              zIndex: 2,
              pointerEvents: "none",
              background: fullHeight
                ? "none"
                : "linear-gradient(to top, #ffffff 0%, rgba(255,255,255,0.9) 45%, rgba(255,255,255,0) 100%)",
            }}
          >
            <ButtonBase
              className="doc-fade-toggle"
              onClick={() => setFullHeight((v) => !v)}
              aria-label={fullHeight ? "Show less" : "Show full height"}
              sx={{
                pointerEvents: "auto",
                opacity: 0,
                transition: "opacity .15s",
                gap: "4px",
                px: 1.25,
                py: "3px",
                borderRadius: "999px",
                fontSize: 11.5,
                fontWeight: 600,
                color: slate[600],
                backgroundColor: "#fff",
                border: `1px solid ${slate[200]}`,
                boxShadow: "0 2px 6px rgba(15,23,42,.08)",
                "&:hover": { color: slate[900], borderColor: slate[300] },
                "&:focus-visible": { opacity: 1 },
              }}
            >
              {fullHeight ? (
                <UnfoldLessRounded sx={{ fontSize: 14 }} />
              ) : (
                <UnfoldMoreRounded sx={{ fontSize: 14 }} />
              )}
              {fullHeight ? "Show less" : "Show full height"}
            </ButtonBase>
          </Flx>
        )}
      </Box>

      <AssociationsEditor
        open={Boolean(assignTarget)}
        loan={loanDrilldown}
        enabledFields={["loanDocuments"]}
        existing={{ loanDocuments: linkedIds }}
        initialQuery={assignTarget?.docType || ""}
        title={
          assignTarget
            ? `Assign existing ${assignTarget.docType}`
            : "Assign existing documents"
        }
        hostLabel={assignTo?.label || (isProperty ? "Property" : "Borrower")}
        saving={linking}
        onSave={handleAssignSave}
        onClose={() => setAssignTarget(null)}
      />
    </Box>
  );
};

RecordDocCoveragePanel.propTypes = {
  /** { type: "borrower"|"property", id, label } — uploads and links land on it. */
  assignTo: PropTypes.object.isRequired,
  /** Doc groups to show; defaults to the record type's list. */
  docGroups: PropTypes.arrayOf(PropTypes.string),
  /** The record's associated, visible documents. */
  files: PropTypes.array,
  /** Ids already associated (incl. hidden) — locked in the assign editor. */
  linkedDocumentIds: PropTypes.array,
  /** Lay the group cards out in wrapping columns (wide surfaces, e.g. a sidebar tab). */
  masonry: PropTypes.bool,
  sx: PropTypes.object,
};

export default RecordDocCoveragePanel;
